import { useFetcher } from '@remix-run/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SearchPageData } from '~/modules-rocco/search/states/search-page-data-atom';
import { searchPageDataLoadingAtom } from '~/modules-rocco/search/states/search-page-data-atom';
import {
    getCachedSearchResults,
    setCachedSearchResults,
} from '~/modules-rocco/search/utils/search-cache';

/**
 * Hook that returns a callback function to prefetch search results.
 * This helps in caching the initial search page data on demand.
 */
export const usePrefetchSearch = (setLoading?: (loading: boolean) => void) => {
    const prefetchSearchFetcher = useFetcher<SearchPageData>();
    // Use a ref to track if data has been fetched and cached to avoid multiple calls from the callback
    const fetchedRef = useRef(false);
    const [searchPageDataLoading, setSearchPageDataLoading] = useAtom(
        searchPageDataLoadingAtom
    );
    const [cacheKey, setCacheKey] = useState('');

    const prefetch = useCallback(
        ({
            url = '/search',
            searchStringForAction = '',
        }: {
            url?: string;
            searchStringForAction?: string;
        }) => {
            if (fetchedRef.current) return; // Already fetched and cached

            // Don't fetch if running in an iframe
            if (typeof window !== 'undefined' && window.self !== window.top) {
                return;
            }

            const baseSearchPath = url; // Use the passed url or default
            if (searchPageDataLoading) return;
            setCacheKey(
                searchStringForAction
                    ? `${baseSearchPath}?search=${searchStringForAction}`
                    : baseSearchPath
            );

            const alreadyCached = getCachedSearchResults(cacheKey);
            if (typeof window === 'undefined') {
                return;
            }
            if (alreadyCached) {
                fetchedRef.current = true; // Mark as fetched if already in cache
                return;
            }

            if (
                prefetchSearchFetcher.state === 'idle' &&
                !prefetchSearchFetcher.data
            ) {
                const formData = new FormData();
                formData.append('searchString', searchStringForAction);
                prefetchSearchFetcher.submit(formData, {
                    method: 'POST',
                    action: baseSearchPath,
                });
                setSearchPageDataLoading(true);
            }
        },
        [
            prefetchSearchFetcher,
            searchPageDataLoading,
            setSearchPageDataLoading,
            setCacheKey,
            cacheKey,
        ]
    );

    useEffect(() => {
        if (
            prefetchSearchFetcher.state === 'idle' &&
            prefetchSearchFetcher.data
        ) {
            // Assuming the callback's parameters (url, searchStringForAction) are stable for this effect
            // or that the default/initial ones used to trigger the fetch are what we want to cache against.
            // For simplicity, using hardcoded default values for cacheKey, align with typical initial prefetch target.

            if (!getCachedSearchResults(cacheKey)) {
                setCachedSearchResults(cacheKey, prefetchSearchFetcher.data);
                fetchedRef.current = true; // Mark as fetched and cached
            }
            setLoading?.(false);
            setSearchPageDataLoading(false);
        }
    }, [
        prefetchSearchFetcher.state,
        prefetchSearchFetcher.data,
        setLoading,
        setSearchPageDataLoading,
        cacheKey,
    ]);

    return prefetch;
};
